import {
	postAuthStartAppleSignInRedirect,
	postAuthStartGoogleSignInRedirect,
	type SocialProviderConfig
} from '@repo/api-portal-sdk/gen'
import { Button } from '@repo/ui/button'
import { FieldSeparator } from '@repo/ui/field'
import { AuthErrorAlert } from '@repo/ui/modules/auth/components/auth-error-alert'
import {
	AppleIcon,
	GoogleIcon
} from '@repo/ui/modules/auth/components/social-provider-icons'
import { useMutation } from '@tanstack/react-query'
import { resolveAppLanguageCode, useTranslation } from '@/lib/i18n'

export function SocialSignIn(props: {
	mode: 'login' | 'register'
	providers: SocialProviderConfig[]
	redirect?: string
	redirectTo: string
}) {
	const { t, i18n } = useTranslation()
	const language = resolveAppLanguageCode(
		i18n.resolvedLanguage ?? i18n.language
	)
	const start = useMutation({
		mutationFn: (provider: SocialProviderConfig['provider']) => {
			const body = {
				callback: props.redirectTo,
				errorCallback: buildSocialErrorCallback(
					props.mode,
					props.redirect
				),
				intent: 'sign-in' as const,
				language
			}
			return provider === 'google'
				? postAuthStartGoogleSignInRedirect({ body })
				: postAuthStartAppleSignInRedirect({ body })
		},
		onSuccess: (response) => window.location.assign(response.data.url)
	})

	return (
		<div className='flex flex-col gap-3'>
			<FieldSeparator className='text-xs [&_[data-slot=field-separator-content]]:bg-card'>
				{t('common:auth.or')}
			</FieldSeparator>
			{start.isError ? (
				<AuthErrorAlert error={start.error} onDismiss={start.reset} />
			) : null}
			<div className='flex flex-col gap-2'>
				{props.providers.map((provider) => (
					<Button
						disabled={start.isPending}
						key={provider.provider}
						onClick={() => start.mutate(provider.provider)}
						size='lg'
						type='button'
						variant='outline'
					>
						{provider.provider === 'google' ? (
							<GoogleIcon />
						) : (
							<AppleIcon />
						)}
						{t(
							props.mode === 'login'
								? 'common:auth.sign_in_with'
								: 'common:auth.register_with',
							{
								provider:
									provider.provider === 'google'
										? 'Google'
										: 'Apple'
							}
						)}
					</Button>
				))}
			</div>
		</div>
	)
}

export function buildSocialErrorCallback(
	mode: 'login' | 'register',
	redirect?: string
) {
	const search = new URLSearchParams({ error: 'social' })
	if (redirect) {
		search.set('redirect', redirect)
	}
	return `/${mode}?${search.toString()}`
}
